import React, { FC, ReactNode, useState } from 'react';

import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';

import { Toast, ToastClose, ToastIcon, ToastMessage, ToastProgress } from './styled';

interface INotification {
  message: string;
  isSuccess?: boolean;
  icon?: ReactNode;
  duration?: number;
}

const DEFAULT_DURATION = 5000;

const ToastAlert: FC<INotification> = ({
  message,
  isSuccess = false,
  icon,
  duration = DEFAULT_DURATION,
}) => {
  const dispatch = useDispatch();
  const [isHovered, setIsHovered] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const close = () => setIsLeaving(true);

  // Runs after the slide-out finishes — only then clear the alert.
  const handleAnimationEnd = (e: React.AnimationEvent) => {
    if (isLeaving && e.target === e.currentTarget) {
      dispatch(setAppAlert({ message: '', isSuccess: false }));
    }
  };

  return (
    <Toast
      $isSuccess={isSuccess}
      $isLeaving={isLeaving}
      role={isSuccess ? 'status' : 'alert'}
      aria-live={isSuccess ? 'polite' : 'assertive'}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setHasFocus(true)}
      onBlur={() => setHasFocus(false)}
      onKeyDown={(e) => e.key === 'Escape' && close()}
      onAnimationEnd={handleAnimationEnd}
    >
      <ToastIcon $isSuccess={isSuccess} aria-hidden>
        {icon ?? (isSuccess ? '✓' : '!')}
      </ToastIcon>
      <ToastMessage>{message}</ToastMessage>
      <ToastClose type="button" aria-label="Close notification" onClick={close}>
        ×
      </ToastClose>
      {/* The countdown: closes the toast when it runs out; paused while the
          user hovers or keyboard-focuses the toast. */}
      <ToastProgress
        $isSuccess={isSuccess}
        $duration={duration}
        $paused={isHovered || hasFocus || isLeaving}
        onAnimationEnd={(e) => {
          e.stopPropagation();
          close();
        }}
      />
    </Toast>
  );
};

// Keyed on the content so a new alert arriving while one is shown restarts the
// countdown — the old timer only ran once per mount, so a second alert could
// vanish almost immediately.
const Notification: FC<INotification> = (props) => (
  <ToastAlert key={`${props.isSuccess}-${props.message}`} {...props} />
);

export default Notification;
